"use client";

/**
 * Route-level error boundary for everything under the root layout — the public
 * marketing pages and /admin alike.
 *
 * Before this existed a client render error unmounted the subtree and was
 * recorded absolutely nowhere: RUM's automatic error telemetry captures window
 * errors and unhandled rejections, but React swallows a render throw it can
 * hand to a boundary, and there was no boundary. The visitor got a blank page
 * and we got silence.
 *
 * It sits at the ROOT segment, so it renders without the site header and
 * footer — those live in app/(site)/layout.tsx, which is one of the children
 * this boundary is standing in for. That is deliberate: if the chrome is what
 * threw, re-rendering it here would throw again.
 */

import { useEffect } from "react";
import { getRumClient } from "@juiceco-io/observability-sdk/react";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // No-op until RUM is initialized (SSR, or an env without a monitor), which
    // is why this is `?.` and not a guard with a fallback: there is nowhere
    // else to send it, and losing the report must never break the fallback UI.
    getRumClient()?.recordError(error);
  }, [error]);

  return (
    <main className="flex min-h-[60vh] items-center bg-paper text-ink">
      <div className="mx-auto w-full max-w-content px-6">
        <span className="font-mono text-xs font-medium uppercase tracking-eyebrow text-brass-deep">
          Something went wrong
        </span>
        <h1 className="mt-3 max-w-[20ch] font-display text-[clamp(28px,3.6vw,42px)] font-semibold leading-[1.1]">
          This page didn&rsquo;t load.
        </h1>
        <p className="mt-4 max-w-[54ch] text-[17px] text-ink-60">
          The problem has been reported. Try again, or head back to the
          homepage.
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={reset}
            className="rounded-sm bg-ink px-5 py-2.5 font-mono text-[12px] uppercase tracking-[0.12em] text-paper"
          >
            Try again
          </button>
          <a
            href="/"
            className="font-mono text-[12px] uppercase tracking-[0.12em] text-brass-deep underline underline-offset-4"
          >
            Back to the homepage
          </a>
        </div>
        {/* The digest is the only handle that ties what the visitor saw to the
            server-side stack in CloudWatch. Worth showing — it is what they
            would quote in a support message. */}
        {error.digest && (
          <p className="mt-8 font-mono text-[11px] tracking-[0.03em] text-ink-60">
            Reference: {error.digest}
          </p>
        )}
      </div>
    </main>
  );
}
